import { SlideHeader } from "../../../SlideHeader";

const GLOSSARY: Array<{
  term: string;
  shortDef: string;
  detail: string;
  apptonomy?: boolean;
}> = [
  {
    term: "MMP",
    shortDef: "Mobile Measurement Partner",
    detail:
      "Attribution platforms that tell publishers which marketing channel/campaign drove which install, open, or in-app purchase. Dominant players: AppsFlyer, Adjust, Singular, Branch. Every publisher running paid UA uses one — without an MMP they're flying blind on what's working.",
  },
  {
    term: "UA Tooling",
    shortDef: "User Acquisition tooling",
    detail:
      "The SaaS layer publishers use to run paid UA campaigns more efficiently: bid optimization, creative testing, audience targeting, cross-channel management. Players include Liftoff, Moloco, AppLovin (demand-side) plus pure tooling layers. Distinct from the media SPEND itself ($78B in 2025) — UA tooling is the software riding on top of that spend.",
  },
  {
    term: "UA Brief Automation",
    shortDef: "Apptonomy's next wedge",
    detail:
      "Generate creative briefs for paid UA campaigns automatically. Today, briefs are hand-written: \"15-sec video, hero feature is X, target keywords A/B/C, audience is power-users in markets D/E.\" The audit signals already driving Apptonomy's listing recommendations — winning keywords, competitor moves, value-prop framing, screenshot patterns — are the same signals that should drive ad-creative briefs. Same data, new artifact. Sells naturally to anyone already buying ASO from us.",
    apptonomy: true,
  },
  {
    term: "MMP Integration",
    shortDef: "Apptonomy closes the loop",
    detail:
      "Ingest attribution data from AppsFlyer / Adjust / etc. into Apptonomy. Today the platform measures listing-side KPIs (impressions, page conversions) from App Store Connect / Google Play Console. With MMP integration, every shipped revision becomes attributable to downstream installs and revenue: \"revision X on Tuesday → campaign-attributed installs lifted Y% → revenue impact was Z.\" Strategically, this is the foothold that lets Apptonomy become the ASO-aware MMP layer over time.",
    apptonomy: true,
  },
];

export default function ExpansionPath() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix F · Expansion Path">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          ASO → UA → MMP → Creative.{" "}
          <span className="text-blue-700">
            Same telemetry, four wedges.
          </span>
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-2xl">
          Glossary of the adjacent tooling categories — and why Apptonomy's
          existing data feed is the natural wedge into each.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-3 md:flex-1 md:grid-cols-2 md:gap-4">
        {GLOSSARY.map((entry) => (
          <GlossaryCard key={entry.term} {...entry} />
        ))}
      </div>

      <div className="mt-5 border-t border-slate-200 pt-4 md:mt-6 md:pt-5">
        <p className="text-balance text-center text-sm leading-snug text-slate-700 md:text-base">
          <span className="font-semibold text-slate-900">From</span>: we help
          your listing convert.{" "}
          <span className="font-semibold text-slate-900">To</span>: we own the
          full publisher growth loop — listing → ad → install → revenue.
        </p>
      </div>
    </div>
  );
}

function GlossaryCard({
  term,
  shortDef,
  detail,
  apptonomy,
}: {
  term: string;
  shortDef: string;
  detail: string;
  apptonomy?: boolean;
}) {
  const wrapClass = apptonomy
    ? "rounded-lg bg-blue-50 p-4 ring-2 ring-blue-300 md:p-5"
    : "rounded-lg bg-white p-4 ring-1 ring-slate-200 md:p-5";
  const termClass = apptonomy
    ? "text-base font-bold text-blue-700 md:text-lg"
    : "text-base font-bold text-slate-900 md:text-lg";
  const shortDefClass = apptonomy
    ? "text-xs italic text-blue-700 md:text-sm"
    : "text-xs italic text-slate-500 md:text-sm";
  const detailClass = apptonomy
    ? "mt-2 text-sm leading-snug text-blue-900 md:text-base"
    : "mt-2 text-sm leading-snug text-slate-700 md:text-base";

  return (
    <div className={wrapClass}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <p className={termClass}>{term}</p>
        <p className={shortDefClass}>{shortDef}</p>
      </div>
      <p className={detailClass}>{detail}</p>
    </div>
  );
}
